import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pagination, Empty, Tag, Button, Modal, Upload, Select, Radio, Alert } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  LoadingOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UploadOutlined,
  EyeOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useAuth } from '../../../assets/utils/useAuth';
import { usePermissions } from '../../../assets/utils/usePermissions';
import { useSearchParams } from 'react-router-dom';
import Draggable from 'react-draggable';
import SearchInput from '../SearchInput';
import PaymentClaimTableRow from './PaymentClaimTableRow';
import { getClaimsArchiveScope, withClaimsArchiveScope, canManageClaimsArchive, buildClaimsArchiveRequest } from '../claimsArchiveHelpers';
import ArchiveClaimsModal from '../ArchiveClaimsModal';
import ClaimsSheetButton from './ClaimsSheetButton';
import './PaymentClaims.css';
import './PaymentDrilldown.css';

const { Option } = Select;

const STATUS_LABELS = {
  0: 'Pending CO',
  1: 'Pending HM',
  2: 'Pending CFO',
  3: 'Paid',
};

function getMonthKey(month) {
  if (!month) return null;
  if (typeof month === 'string') return month;
  return month.month || month.key || null;
}

function formatMonthLabel(month) {
  const key = getMonthKey(month);
  if (!key) return 'Month';
  return dayjs(`${key}-01`).format('MMMM YYYY');
}

function ClaimRowSkeleton() {
  return (
    <div className="drill-row-skeleton" aria-hidden>
      <div className="drill-row-skeleton__block" style={{ width: 32, height: 32, borderRadius: 8 }} />
      <div className="drill-row-skeleton__block" style={{ width: '32%' }} />
      <div className="drill-row-skeleton__block" style={{ width: 70, marginLeft: 'auto' }} />
      <div className="drill-row-skeleton__block" style={{ width: 90 }} />
    </div>
  );
}

/**
 * PaymentClaims - Displays claims for a pending status (0, 1, 2)
 * Used for Pending CO, Pending HM, and Pending CFO statuses
 */
const PaymentClaims = ({ segment, month, facilityId, facilityName, onClaimClick }) => {
  const { user } = useAuth();
  const { permissions, hasAnyPermissionWithLegacy } = usePermissions();
  const [searchParams] = useSearchParams();
  const CLAIMS_SEARCH_PARAM = 'paymentClaimsSearch';
  const CLAIMS_PAGE_PARAM = 'paymentClaimsPage';
  const CLAIMS_PAGE_SIZE_PARAM = 'paymentClaimsPageSize';
  const [claims, setClaims] = useState([]);
  const [facility, setFacility] = useState(null);
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get search query from URL
  const searchQuery = searchParams.get(CLAIMS_SEARCH_PARAM) || '';
  const [visiblePaidModal, setVisiblePaidModal] = useState(false);
  /** 'upload' = with payment advice, 'without' = mark Paid now, advice later */
  const [payMethod, setPayMethod] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const draggleRef = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: null, // Will be set to default based on segment
    sortOrder: 'desc',
  });
  const fetchingRef = useRef(false);
  const initialLoadRef = useRef(true);
  const hasSearchedRef = useRef(false);
  const [selectedClaimIds, setSelectedClaimIds] = useState([]);
  /** null | 'pay' */
  const [selectionMode, setSelectionMode] = useState(null);
  // Controls whether the modal pays a specific selection or pays everything matching
  // the facility + date/month filters. When pay-all is used we must never include any claim id.
  const [payAllMode, setPayAllMode] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveModalMode, setArchiveModalMode] = useState('archive');
  const [archiveRequest, setArchiveRequest] = useState(null);
  const archiveScope = getClaimsArchiveScope(searchParams);
  
  // Finance/payment capability: may mark claims paid from the Pending CFO queue.
  // Legacy Head of Finance (5) keeps it while the RBAC permission map is still empty.
  const canPayClaims = hasAnyPermissionWithLegacy(['claims.payment'], [5]);
  const canArchiveClaims = canManageClaimsArchive(user, permissions);

  // Get default sortBy - always use claims_date (when filed) to show newest first
  const getDefaultSortBy = (seg) => {
    // Always sort by claims_date (when filed) to show newest first
    return 'claims_date';
  };

  // Get allowed sort fields for segment
  const getAllowedSortFields = (seg) => {
    return [
      { value: 'claims_date', label: 'Claims Date' },
      { value: 'encounter_date', label: 'Encounter Date' },
      { value: 'date_forwarded_hm', label: 'Date Forwarded to HM' },
      { value: 'date_forwarded_cfo', label: 'Date Forwarded to CFO' },
      { value: 'created_at', label: 'Created At' },
      { value: 'tat', label: 'Turnaround' },
    ];
  };

  // Initialize sort config when segment changes
  useEffect(() => {
    if (segment !== null && segment !== undefined) {
      setSortConfig({
        sortBy: getDefaultSortBy(segment),
        sortOrder: 'desc',
      });
    }
  }, [segment]);

  // Reset pagination when drill-down context changes to avoid stale page=2+ requests.
  useEffect(() => {
    if (segment !== null && segment !== undefined && month && facilityId) {
      setPagination(prev => ({ ...prev, current: 1 }));
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(CLAIMS_SEARCH_PARAM);
      newParams.set(CLAIMS_PAGE_PARAM, '1');
      newParams.delete(CLAIMS_PAGE_SIZE_PARAM);
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
      initialLoadRef.current = true;
      hasSearchedRef.current = false;
    }
  }, [segment, month, facilityId, searchParams]);

  const fetchClaims = useCallback(async (page = 1, search = '') => {
    if (!segment && segment !== 0) return;
    if (!month || !facilityId) return;
    
    // Prevent duplicate calls
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    // Extract month key (YYYY-MM format)
    const monthKey = getMonthKey(month);
    if (!monthKey) {
      notification.error({
        message: 'Error',
        description: 'Invalid month format',
      });
      return;
    }

    setLoading(true);
    try {
      // Use sort config or fallback to defaults
      const sortBy = sortConfig.sortBy || getDefaultSortBy(segment);
      const sortOrder = sortConfig.sortOrder || 'desc';
      
      const params = withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder,
        sort_by: sortBy,
        sort_dir: sortOrder,
        ...(search && search.trim() ? { search: search.trim() } : {}),
      }, archiveScope);
      
      // Add sorting parameters with segment-specific defaults (newest first)
      const response = await lib.getPaymentStatusClaims(segment, monthKey, facilityId, params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch claims',
        });
        return;
      }

      const responseData = response?.data || {};
      const claimsList = Array.isArray(responseData.data) ? responseData.data : [];
      const facilitiesList = Array.isArray(responseData.facilities) ? responseData.facilities : [];
      
      setClaims(claimsList);
      if (facilitiesList.length > 0) {
        setFacility(facilitiesList[0]);
      }
      setSearchMeta(responseData.search || null); // Store search metadata
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || 1,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || claimsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching claims:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch claims',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [segment, month, facilityId, pagination.pageSize, sortConfig.sortBy, sortConfig.sortOrder, archiveScope]); // eslint-disable-line react-hooks/exhaustive-deps -- searchQuery from URL

  useEffect(() => {
    if (segment !== null && segment !== undefined && month && facilityId) {
      const page = parseInt(searchParams.get(CLAIMS_PAGE_PARAM) || '1');
      const urlSearch = searchParams.get(CLAIMS_SEARCH_PARAM) || '';
      setPagination(prev => ({ ...prev, current: page }));
      
      const shouldUseSearch = hasSearchedRef.current || (!initialLoadRef.current && urlSearch.trim());
      fetchClaims(page, shouldUseSearch ? urlSearch : '');
      
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
      }
    }
  }, [segment, month, facilityId, sortConfig, fetchClaims, searchParams]);

  const handleSortChange = (field, order) => {
    setSortConfig({
      sortBy: field,
      sortOrder: order,
    });
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    // Use search from URL if it exists (user may have searched at this level)
    const currentSearch = searchParams.get(CLAIMS_SEARCH_PARAM) || '';
    fetchClaims(page, currentSearch);
    // Update URL with new page
    const newParams = new URLSearchParams(searchParams);
    newParams.set(CLAIMS_PAGE_PARAM, page.toString());
    if (pageSize) {
      newParams.set(CLAIMS_PAGE_SIZE_PARAM, pageSize.toString());
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
  };

  const handleSearch = (query) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    hasSearchedRef.current = true;
    initialLoadRef.current = false;
    // Trigger an immediate fetch so user sees results without waiting
    fetchClaims(1, query);
  };

  const getClaimKey = (claim) => claim.claims_id || claim.checkin_id;

  const handleClaimClick = (claim) => {
    if (onClaimClick) {
      onClaimClick(claim);
    }
  };

  const toggleClaimSelection = (claim, checked) => {
    const key = getClaimKey(claim);
    if (!key) return;
    setSelectedClaimIds(prev => {
      if (checked) {
        if (prev.includes(key)) return prev;
        return [...prev, key];
      }
      return prev.filter(id => id !== key);
    });
  };

  const handleMarkAsPaid = () => {
    // Pay selection flow (never include facility-level pay_all=true here)
    setPayAllMode(false);
    setPayMethod('upload');
    setVisiblePaidModal(true);
    setUploaded(false);
  };

  const handleCancelPaid = () => {
    setVisiblePaidModal(false);
    setPayAllMode(false);
    setUploaded(false);
  };

  const openArchiveModal = (request, mode = 'archive') => {
    setArchiveRequest(request);
    setArchiveModalMode(mode);
    setArchiveModalOpen(true);
  };

  const handleUploadDone = (paymentData) => {
    setVisiblePaidModal(false);
    setUploaded(false);
    
    // Refresh the claims list - preserve search
    const currentSearch = searchParams.get(CLAIMS_SEARCH_PARAM) || '';
    fetchClaims(1, currentSearch);

    const monthKey = getMonthKey(month);
    if (canArchiveClaims && monthKey && facilityId && segment === 2) {
      notification.info({
        message: 'Payment complete',
        description: 'You can archive this facility’s claims for the month when you are ready.',
        placement: 'topLeft',
        duration: 4,
      });
      openArchiveModal(buildClaimsArchiveRequest({
        scope: 'facility',
        month: monthKey,
        facilityId,
        // This list is one payment stage; archiving from it must stay in it.
        segment,
      }));
    }
  };

  const onStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();

    if (!targetRect) {
      return;
    }

    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  // Get claim IDs for the batch (selected claims)
  // Prefer claims_id (database ID) over checkin_id (check-in code)
  // The V2 endpoint accepts both formats, but claims_id is the primary identifier
  const getClaimIds = () => selectedClaimIds;

  // Helpers to derive date/month filters for pay-all
  const getDateFiltersForPayAll = () => {
    const filters = {};
    const monthKey = getMonthKey(month);
    if (!monthKey) return filters;

    // monthKey is YYYY-MM
    const [yearStr, monthStr] = monthKey.split('-');
    const year = parseInt(yearStr, 10);
    const monthNum = parseInt(monthStr, 10);
    if (!Number.isNaN(year) && !Number.isNaN(monthNum)) {
      filters.month = monthNum;
      filters.year = year;
    }

    return filters;
  };

  const displayFacilityName = facilityName || facility?.facility_name || 'Facility';
  const monthLabel = formatMonthLabel(month);
  const statusLabel = STATUS_LABELS[segment] || 'Status';
  const facilityAddress = facility?.facility_address
    ? [facility.facility_address, facility.facility_city, facility.facility_state].filter(Boolean).join(', ')
    : null;
  const claimCountLabel = loading
    ? 'Loading…'
    : `${pagination.total.toLocaleString()} claim${pagination.total === 1 ? '' : 's'}`;

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(CLAIMS_SEARCH_PARAM);
    newParams.set(CLAIMS_PAGE_PARAM, '1');
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
    handleSearch('');
  };

  if (!segment && segment !== 0 || !month || !facilityId) {
    return <Empty description="Missing required parameters" />;
  }

  return (
    <div className="payment-claims">
      <div className="drill-panel">
        <div className="drill-panel__head">
          <div>
            <h2>
              {displayFacilityName}
              {selectionMode ? (
                <span className="payment-claims-header__pill payment-claims-header__pill--accent" style={{ marginLeft: 8 }}>
                  {selectedClaimIds.length} selected
                </span>
              ) : null}
            </h2>
            <p>{statusLabel} · {monthLabel} · {claimCountLabel}{facilityAddress ? ` · ${facilityAddress}` : ''}</p>
          </div>
        </div>

      <div className="payment-claims-toolbar">
        <div className="payment-claims-toolbar__search">
          <SearchInput
            placeholder="Search patient, insurance, claim ID…"
            onSearch={handleSearch}
            loading={loading}
            searchParamName={CLAIMS_SEARCH_PARAM}
            pageParamName={CLAIMS_PAGE_PARAM}
          />
        </div>

        <div className="payment-claims-toolbar__sort">
          <Select
            value={sortConfig.sortBy || getDefaultSortBy(segment)}
            onChange={(value) => handleSortChange(value, sortConfig.sortOrder)}
            className="payment-claims-sort__select"
            size="small"
          >
            {getAllowedSortFields(segment).map((field) => (
              <Option key={field.value} value={field.value}>
                {field.label}
              </Option>
            ))}
          </Select>
          <Button
            icon={sortConfig.sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
            onClick={() =>
              handleSortChange(
                sortConfig.sortBy || getDefaultSortBy(segment),
                sortConfig.sortOrder === 'desc' ? 'asc' : 'desc'
              )
            }
            size="small"
            title={sortConfig.sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          />
        </div>

        <div className="payment-claims-toolbar__actions">
          <ClaimsSheetButton
            segment={segment}
            level="claims"
            month={getMonthKey(month)}
            facilityId={facilityId}
            disabled={loading}
          />

          {claims.length > 0 && canPayClaims && segment === 2 ? (
            <>
              {!selectionMode ? (
                <>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      setPayAllMode(false);
                      setSelectionMode('pay');
                      setSelectedClaimIds([]);
                    }}
                  >
                    Pay selection
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setPayAllMode(true);
                      setSelectionMode(null);
                      setSelectedClaimIds([]);
                      setPayMethod('upload');
                      setVisiblePaidModal(true);
                    }}
                  >
                    Pay all
                  </Button>
                </>
              ) : selectionMode === 'pay' ? (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      if (selectedClaimIds.length === claims.length) {
                        setSelectedClaimIds([]);
                      } else {
                        setSelectedClaimIds(claims.map((c) => getClaimKey(c)).filter(Boolean));
                      }
                    }}
                  >
                    {selectedClaimIds.length === claims.length ? 'Clear' : 'All'}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={handleMarkAsPaid}
                    className="payment-claims-pay__confirm"
                    disabled={selectedClaimIds.length === 0}
                  >
                    Pay{selectedClaimIds.length ? ` (${selectedClaimIds.length})` : ''}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectionMode(null);
                      setSelectedClaimIds([]);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : null}
            </>
          ) : null}

          {canArchiveClaims && selectionMode !== 'pay' ? (
            <Button
              size="small"
              onClick={() => openArchiveModal(buildClaimsArchiveRequest({
                scope: 'facility',
                month: getMonthKey(month),
                facilityId,
                // Scoped to the stage this list is showing, not to every stage.
                segment,
              }), archiveScope === 'archived' ? 'unarchive' : 'archive')}
            >
              {archiveScope === 'archived' ? `Unarchive ${statusLabel} this month` : `Archive ${statusLabel} this month`}
            </Button>
          ) : null}
        </div>
      </div>

      {searchMeta?.applied ? (
        <div className="payment-claims-search-banner" role="status">
          <span>
            Found <strong>{searchMeta.results_count || 0}</strong> result
            {searchMeta.results_count !== 1 ? 's' : ''} for{' '}
            <Tag color="blue">{searchMeta.query}</Tag>
          </span>
          <button type="button" className="payment-claims-search-banner__clear" onClick={clearSearch}>
            <CloseOutlined aria-hidden /> Clear
          </button>
        </div>
      ) : null}

      <div className="payment-claims-body">
        {loading ? (
          <div aria-label="Loading claims">
            {Array.from({ length: 6 }).map((_, i) => (
              <ClaimRowSkeleton key={`skel-${i}`} />
            ))}
          </div>
        ) : claims.length === 0 ? (
          <div className="claims-empty">
            <FileTextOutlined style={{ fontSize: 28, color: '#94a3b8' }} />
            <p className="claims-empty__title">
              {searchQuery ? `No claims found for "${searchQuery}"` : 'No claims in this facility'}
            </p>
            <p className="claims-empty__desc">
              {searchQuery
                ? 'Try a different patient name, insurance number, or claim ID.'
                : `No claims match ${displayFacilityName} for ${monthLabel} in ${statusLabel}.`}
            </p>
          </div>
        ) : (
          <div className="claims-table-wrap">
            <table className="claims-drill-table" aria-label="Claims">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Encounter</th>
                  <th className="num">Submitted</th>
                  <th className="num">PA approved</th>
                  <th className="num">Payable</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => {
                  const claimKey = getClaimKey(claim);
                  return (
                    <PaymentClaimTableRow
                      key={claimKey}
                      claim={claim}
                      segment={segment}
                      selectionMode={selectionMode !== null}
                      isSelected={selectedClaimIds.includes(claimKey)}
                      onToggleSelect={(checked) => toggleClaimSelection(claim, checked)}
                      onClick={() => handleClaimClick(claim)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && pagination.total > pagination.pageSize ? (
          <div className="payment-claims-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        ) : null}
      </div>
      </div>

      <Modal
        title={
          <div
            style={{
              width: '100%',
              cursor: 'move',
            }}
            onMouseOver={() => {
              if (disabled) {
                setDisabled(false);
              }
            }}
            onMouseOut={() => {
              setDisabled(true);
            }}
            onFocus={() => { }}
            onBlur={() => { }}
          >
            Mark as Paid
          </div>
        }
        width={440}
        open={visiblePaidModal}
        onOk={handleUploadDone}
        onCancel={handleCancelPaid}
        destroyOnClose
        okText="Done"
        footer={payMethod === 'without' ? null : undefined}
        okButtonProps={{
          disabled: !uploaded,
          style: uploaded ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}
        }}
        modalRender={(modal) => (
          <Draggable
            disabled={disabled}
            bounds={bounds}
            onStart={(event, uiData) => onStart(event, uiData)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
      >
        <Radio.Group
          className="payment-pay-method"
          value={payMethod}
          onChange={(e) => setPayMethod(e.target.value)}
          disabled={uploading || uploaded}
          style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}
        >
          <Radio value="upload">Upload Payment Advice</Radio>
          <Radio value="without">Mark as Paid Without Payment Advice</Radio>
        </Radio.Group>
        {payMethod === 'upload' ? (
          <div className='form-group' style={{ width: 'fit-content', margin: '5px auto' }}>
            <PaymentDocumentUploader 
              // If pay-all is used, we must never include claim ids in the request.
              claimItems={payAllMode ? [] : getClaimIds()} 
              isPayAllMode={payAllMode}
              facilityId={facilityId}
              monthFilters={getDateFiltersForPayAll()}
              done={handleUploadDone}
              onUploading={setUploading}
              onUploaded={setUploaded}
            />
          </div>
        ) : (
          <MarkPaidWithoutAdvice
            claimItems={payAllMode ? [] : getClaimIds()}
            isPayAllMode={payAllMode}
            facilityId={facilityId}
            monthFilters={getDateFiltersForPayAll()}
            done={handleUploadDone}
            onCancel={handleCancelPaid}
          />
        )}
      </Modal>

      <ArchiveClaimsModal
        open={archiveModalOpen}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
        }}
        request={archiveRequest}
        mode={archiveModalMode}
        segment={segment}
        onSuccess={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
          setSelectionMode(null);
          setSelectedClaimIds([]);
          const currentSearch = searchParams.get(CLAIMS_SEARCH_PARAM) || '';
          fetchClaims(pagination.current, currentSearch);
          notification.success({ message: 'Claims updated', placement: 'topLeft' });
        }}
      />
    </div>
  );
};

/**
 * Mark as Paid without payment advice: the claims move to Paid now and show
 * "Paid — Payment Advice Pending" until the advice is uploaded from the batch.
 */
const MarkPaidWithoutAdvice = ({ claimItems, isPayAllMode = false, facilityId, monthFilters = {}, done, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const count = Array.isArray(claimItems) ? claimItems.length : 0;

  const submit = () => {
    if (!isPayAllMode && count === 0) {
      notification.warning({ message: 'No claims selected', placement: 'topLeft' });
      return;
    }
    setLoading(true);
    lib.payClaimsV2({
      claimsIds: !isPayAllMode ? claimItems : null,
      facilityId: isPayAllMode ? facilityId : null,
      filters: isPayAllMode ? monthFilters : {},
      withoutAdvice: true,
    })
      .then((response) => {
        const body = response?.data ?? response;
        if (response?.error ?? body?.error) {
          notification.error({
            message: 'Payment Failed',
            description: response?.message ?? body?.message ?? 'Payment failed',
            placement: 'topLeft',
            duration: 6,
          });
          setLoading(false);
          return;
        }
        notification.success({
          message: 'Marked as Paid',
          description: `${body?.data?.claims_count ?? count} claim(s) marked Paid — Payment Advice Pending. Upload the advice from the paid batch when you have it.`,
          placement: 'topLeft',
          duration: 6,
        });
        setLoading(false);
        if (done) done(body?.data);
      })
      .catch((error) => {
        setLoading(false);
        notification.error({
          message: 'Payment Error',
          description: error?.response?.data?.message || error?.message || 'An error occurred while processing payment',
          placement: 'topLeft',
          duration: 6,
        });
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Alert
        type="info"
        showIcon
        message={isPayAllMode ? 'All pending claims for this facility and month will be marked Paid.' : `${count} claim(s) will be marked Paid.`}
        description='They will show "Paid — Payment Advice Pending" until the payment advice is uploaded from the paid batch.'
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="primary" icon={<CheckCircleOutlined />} loading={loading} onClick={submit}>
          Mark as Paid
        </Button>
      </div>
    </div>
  );
};

// Payment Document Uploader Component (V2 Payment Endpoint)
// Supports two modes:
// - Selected claims: send claims_ids
// - Pay all for facility: pay_all=true + facility/date filters
const PaymentDocumentUploader = ({ claimItems, isPayAllMode = false, facilityId, monthFilters = {}, done, onUploading, onUploaded }) => {
  const [loading, setLoading] = useState(false);
  const [uploaded, setUploadedLocal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);

  // Validate file type and size
  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    // PDF-only (payment evidence / advice)
    const fileName = (file.name || '').toLowerCase();
    const isPdfByMime = file.type === 'application/pdf';
    const isPdfByExt = fileName.endsWith('.pdf');
    const isPdf = isPdfByMime || isPdfByExt;

    if (!isPdf) {
      notification.error({
        message: 'Invalid file type',
        description: 'Allowed file type: PDF',
        placement: 'topLeft',
        duration: 5
      });
      return false;
    }
    
    if (file.size > maxSize) {
      notification.error({ 
        message: 'File too large',
        description: 'File size must not exceed 10MB',
        placement: 'topLeft',
        duration: 5
      });
      return false;
    }
    
    return true;
  };

  const handleFileChange = (info) => {
    const file = info.file;
    
    // Only handle when file is selected (not during upload)
    if (file.status === 'done' || file.status === 'uploading') {
      return;
    }
    
    // Validate file before setting it
    if (!validateFile(file.originFileObj || file)) {
      return;
    }
    
    setSelectedFile(file.originFileObj || file);
    setUploadedLocal(false);
    setUploadedFileUrl(null);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      notification.warning({
        message: 'No file selected',
        description: 'Please select a payment document first',
        placement: 'topLeft',
      });
      return;
    }

    setLoading(true);
    setUploadedLocal(false);
    if (onUploading) onUploading(true);

    const hasClaimItems = Array.isArray(claimItems) && claimItems.length > 0;

    if (!isPayAllMode && !hasClaimItems) {
      notification.warning({
        message: 'No claims selected',
        description: 'Please select one or more claims, or use "Pay All for this Facility".',
        placement: 'topLeft',
      });
      setLoading(false);
      if (onUploading) onUploading(false);
      return;
    }

    // Use V2 payment endpoint
    lib.payClaimsV2({
      claimsIds: !isPayAllMode && hasClaimItems ? claimItems : null,
      facilityId: isPayAllMode ? facilityId : null,
      filters: isPayAllMode ? monthFilters : {},
      paymentEvidenceFile: selectedFile,
    })
      .then(response => {
        // `myApi` sometimes returns a normalized error object like:
        // { error: 1, message: '...', data: [] }
        // In that case, `response.data` exists but is an array, so we must
        // look at `response.error` instead of only `body.error`.
        const body = response?.data ?? response;
        const hasError = Boolean(response?.error ?? body?.error);

        const normalizeErrorMessage = (msg) => {
          const m = String(msg || '').trim();
          // Backend message is a bit technical; make it user-friendly.
          if (m.toLowerCase().includes('no pending claims found to pay for this facility')) {
            return 'There are no pending claims to pay for this facility in this month.';
          }
          return m || 'Payment failed';
        };

        if (hasError) {
          setLoading(false);
          setUploadedLocal(false);
          if (onUploading) onUploading(false);
          if (onUploaded) onUploaded(false);
          
          // Handle specific error messages
          let errorMessage = normalizeErrorMessage(
            response?.message ?? body?.message
          );

          if (body?.data?.invalid_claims) {
            const invalidClaims = body.data.invalid_claims.map(c => c.claims_id).join(', ');
            errorMessage = `${body.message}. Invalid claims: ${invalidClaims}`;
          } else if (body?.data?.missing_claims_ids) {
            const missingClaims = body.data.missing_claims_ids.join(', ');
            errorMessage = `${body.message}. Missing claims: ${missingClaims}`;
          }
          
          notification.error({
            message: 'Payment Failed',
            description: errorMessage,
            placement: 'topLeft',
            duration: 6
          });
        } else {
          // Success
          const paymentBatch = body?.data?.payment_batch;
          const claimsCount = body?.data?.claims_count || claimItems.length;
          const fileUrl = body?.data?.payment_evidence_file;
          
          notification.success({
            message: 'Payment Successful',
            description: `Successfully paid ${claimsCount} claim(s). Payment batch: ${paymentBatch}`,
            placement: 'topLeft',
            duration: 5
          });
          
          setLoading(false);
          setUploadedLocal(true);
          setUploadedFileUrl(fileUrl);
          if (onUploading) onUploading(false);
          if (onUploaded) onUploaded(true);
          
          // Call done callback after a short delay to allow notification to show
          setTimeout(() => {
            if (done) done(body.data);
          }, 500);
        }
      })
      .catch(error => {
        console.error('Payment error:', error);
        setLoading(false);
        setUploadedLocal(false);
        if (onUploading) onUploading(false);
        if (onUploaded) onUploaded(false);
        
        // Extract error message from response
        const errorMessage = error?.response?.data?.message || 
                            error?.message || 
                            'An error occurred while processing payment';
        
        notification.error({
          message: 'Payment Error',
          description: errorMessage,
          placement: 'topLeft',
          duration: 6
        });
      });
  };

  const beforeUpload = () => {
    // Prevent auto upload - we'll handle it manually
    return false;
  };

  const uploadButton = (
    <div>
      {selectedFile ? <FileTextOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>
        {selectedFile ? selectedFile.name.substring(0, 15) + (selectedFile.name.length > 15 ? '...' : '') : 'Select File'}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
      <Upload
        name="payment-document"
        listType="picture-card"
        className="avatar-uploader"
        showUploadList={false}
        beforeUpload={beforeUpload}
        onChange={handleFileChange}
        disabled={loading || uploaded}
        accept=".pdf,application/pdf"
      >
        {uploadButton}
      </Upload>
      
      {selectedFile && !uploaded && (
        <Button
          type="primary"
          icon={loading ? <LoadingOutlined /> : <UploadOutlined />}
          onClick={handleUpload}
          loading={loading}
          disabled={loading || uploaded}
          style={{ width: '100%' }}
        >
          {loading ? 'Uploading...' : 'Upload Payment Document'}
        </Button>
      )}
      
      {uploaded && uploadedFileUrl && (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            // Construct full URL if it's a relative path
            const fullUrl = uploadedFileUrl.startsWith('http') 
              ? uploadedFileUrl 
              : `${window.location.origin}${uploadedFileUrl.startsWith('/') ? '' : '/'}${uploadedFileUrl}`;
            window.open(fullUrl, '_blank');
          }}
          style={{ width: '100%' }}
        >
          View Uploaded Document
        </Button>
      )}
    </div>
  );
};

export default PaymentClaims;

